import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from './ui/dialog';
import { Download, Share2, Plus, MoreVertical, Monitor, CheckCircle } from 'lucide-react';

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const detectPlatform = () => {
  const ua = navigator.userAgent || '';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
};

const Step = ({ n, children }) => (
  <li className="flex items-start gap-2">
    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#00C0C0] text-[#001828] text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm leading-snug">{children}</span>
  </li>
);

/**
 * InstallAppButton - always-functional "Install App" control.
 * - If the browser supports the native install prompt (Android/desktop Chrome/Edge), it triggers it.
 * - Otherwise (or if the prompt isn't available), it opens clear, device-specific
 *   step-by-step instructions so the user can always install the app.
 * Hides only when the app is already installed (running standalone).
 */
export const InstallAppButton = ({ variant = 'default', className = '' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    const onPrompt = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome !== 'accepted') setHelpOpen(true);
      } catch (e) {
        setHelpOpen(true);
      }
      setDeferredPrompt(null);
    } else {
      // No native prompt available -> always show instructions so it never "does nothing"
      setHelpOpen(true);
    }
  };

  const platform = detectPlatform();

  return (
    <>
      <Button variant={variant} onClick={handleClick} className={className} data-testid="install-app-btn">
        <Download className="h-4 w-4 mr-2" />
        Install App
      </Button>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Install the Chinook app</DialogTitle>
            <DialogDescription>
              Add this portal to your device so it opens in its own window like a native app.
            </DialogDescription>
          </DialogHeader>

          {platform === 'ios' && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
                <Share2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                On iPhone, apps can only be installed from <strong>&nbsp;Safari</strong>&nbsp;— not Chrome. Open this site in Safari first.
              </div>
              <ol className="space-y-3">
                <Step n="1">In <strong>Safari</strong>, tap the <Share2 className="h-4 w-4 inline mx-0.5" /> <strong>Share</strong> button (bottom center).</Step>
                <Step n="2">Scroll down and choose <Plus className="h-4 w-4 inline mx-0.5" /> <strong>Add to Home Screen</strong>.</Step>
                <Step n="3">Tap <strong>Add</strong>. Chinook opens full-screen from your home screen — like a real app.</Step>
              </ol>
            </div>
          )}

          {platform === 'android' && (
            <ol className="space-y-3">
              <Step n="1">In <strong>Chrome</strong>, tap the <MoreVertical className="h-4 w-4 inline mx-0.5" /> menu (top-right).</Step>
              <Step n="2">Tap <strong>Add to Home screen</strong> (or <strong>Install app</strong>).</Step>
              <Step n="3">Tap <strong>Install</strong> / <strong>Add</strong>. The app is added to your device.</Step>
            </ol>
          )}

          {platform === 'desktop' && (
            <ol className="space-y-3">
              <Step n="1">In <strong>Chrome</strong> or <strong>Edge</strong>, look for the <Monitor className="h-4 w-4 inline mx-0.5" /> <strong>install icon</strong> at the right end of the address bar.</Step>
              <Step n="2">No icon? Open the <MoreVertical className="h-4 w-4 inline mx-0.5" /> menu → <strong>Cast, save, and share</strong> → <strong>Install page as app…</strong> (Edge: <strong>Apps → Install this site as an app</strong>).</Step>
              <Step n="3">Click <strong>Install</strong>. Chinook opens in its own window.</Step>
            </ol>
          )}

          <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
            <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
            Works offline-friendly and sends you notifications once installed.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallAppButton;
